import React, { useEffect, useState } from 'react';
import { Phone, Mic, Bot } from 'lucide-react';
import api from '../../services/api';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
import alertService from '../../utils/alert';
import VoiceCampaignsPanel from './VoiceCampaignsPanel';

const STATUS_BADGE = {
  initiated: 'bg-[#FFF4E5] text-[#8C4B02]',
  queued: 'bg-[#FFF4E5] text-[#8C4B02]',
  ringing: 'bg-[#D6EAFB] text-[#0176D3]',
  'in-progress': 'bg-[#E3F3E8] text-[#036B3A]',
  completed: 'bg-[#F3F2F2] text-[#5C5C5C]',
  missed: 'bg-[#FDE7E9] text-[#BA0517]',
  busy: 'bg-[#FFF4E5] text-[#8C4B02]',
  failed: 'bg-[#FDE7E9] text-[#8E030F]',
  'no-answer': 'bg-[#FDE7E9] text-[#BA0517]',
};

const SENTIMENT_ICON = { positive: '😊', neutral: '😐', negative: '😟' };

function CallModal({ call, onClose }) {
  if (!call) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg max-h-[100dvh] overflow-y-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[16px] font-bold text-[#2E2E2E]">Call Details</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#444444]">✕</button>
        </div>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#F7F9FB] rounded-lg p-3">
              <p className="text-xs text-[#706E6B] mb-0.5">Direction</p>
              <p className="font-medium capitalize">{call.direction}</p>
            </div>
            <div className="bg-[#F7F9FB] rounded-lg p-3">
              <p className="text-xs text-[#706E6B] mb-0.5">Duration</p>
              <p className="font-medium">{call.duration ? `${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, '0')}` : '—'}</p>
            </div>
            <div className="bg-[#F7F9FB] rounded-lg p-3">
              <p className="text-xs text-[#706E6B] mb-0.5">From</p>
              <p className="font-medium font-mono">{call.from || '—'}</p>
            </div>
            <div className="bg-[#F7F9FB] rounded-lg p-3">
              <p className="text-xs text-[#706E6B] mb-0.5">To</p>
              <p className="font-medium font-mono">{call.to || '—'}</p>
            </div>
          </div>
          {call.sentiment && (
            <div className="bg-[#F7F9FB] rounded-lg p-3">
              <p className="text-xs text-[#706E6B] mb-0.5">Sentiment</p>
              <p className="font-medium capitalize">{SENTIMENT_ICON[call.sentiment]} {call.sentiment}</p>
            </div>
          )}
          {(call.disposition || call.answeredBy === 'voicemail' || call.qa?.score) && (
            <div className="grid grid-cols-2 gap-3">
              {(call.disposition || call.answeredBy === 'voicemail') && <div className="bg-[#F7F9FB] rounded-lg p-3"><p className="text-xs text-[#706E6B] mb-0.5">Outcome</p><p className="font-medium">{call.disposition || 'voicemail'}</p></div>}
              {call.qa?.score && <div className="bg-[#F7F9FB] rounded-lg p-3"><p className="text-xs text-[#706E6B] mb-0.5">QA score</p><p className="font-medium">{call.qa.score}/10</p>{call.qa.notes && <p className="text-xs text-[#706E6B] mt-1">{call.qa.notes}</p>}</div>}
            </div>
          )}
          {call.extracted && Object.keys(call.extracted).length > 0 && (
            <div className="bg-[#F7F9FB] rounded-lg p-3">
              <p className="text-xs text-[#706E6B] mb-1 font-medium">Collected on the call</p>
              {Object.entries(call.extracted).map(([k, v]) => <p key={k} className="text-xs text-[#444444]"><span className="font-semibold">{k}:</span> {String(v)}</p>)}
            </div>
          )}
          {call.summary && (
            <div className="bg-[#EAF4FE] rounded-lg p-3">
              <p className="text-xs text-[#0176D3] mb-1 font-medium">AI Summary</p>
              <p className="text-[#444444] leading-relaxed">{call.summary}</p>
            </div>
          )}
          {call.transcript && (
            <div className="bg-[#F7F9FB] rounded-lg p-3 max-h-40 overflow-y-auto">
              <p className="text-xs text-[#706E6B] mb-1 font-medium">Transcript</p>
              <p className="text-[#444444] text-xs leading-relaxed whitespace-pre-wrap">{call.transcript}</p>
            </div>
          )}
          {call.recordingLink && (
            <div className="bg-[#F7F9FB] rounded-lg p-3">
              <p className="text-xs text-[#706E6B] mb-1 font-medium">Recording</p>
              <audio controls src={call.recordingLink} className="w-full" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VoicePage() {
  const { hasAccess, plan, loading: featureLoading } = useFeatureAccess('voice_ai');
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ direction: '', status: '' });
  const [dialNumber, setDialNumber] = useState('');
  const [dialing, setDialing] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [tab, setTab] = useState('calls');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filter).filter(([, v]) => v))).toString();
      const { data } = await api.get(`/voice/calls?${params}`);
      setCalls(data.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess) load();
  }, [filter, hasAccess]);

  if (featureLoading) return null;

  if (!hasAccess) {
    return (
      <FeatureLockedPage
        title="Voice AI"
        subtitle="Call recording, transcription, and AI-powered call intelligence"
        icon={Phone}
        plan={plan}
        featureName="Voice AI"
        description="Upgrade to unlock call recording, automatic transcription, AI call summaries, sentiment analysis, and missed-call WhatsApp recovery."
        highlights={[
          { icon: Mic, label: 'Auto transcription', desc: 'Every call transcribed with speaker separation' },
          { icon: Bot, label: 'AI summaries', desc: 'Instant summary and sentiment for every call' },
          { icon: Phone, label: 'Missed-call recovery', desc: 'Automatic WhatsApp follow-up on missed calls' },
        ]}
      />
    );
  }

  const dial = async () => {
    if (!dialNumber.trim()) return;
    setDialing(true);
    try {
      await api.post('/voice/calls', { to: dialNumber });
      setDialNumber('');
      setTimeout(load, 2000);
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to initiate call');
    } finally {
      setDialing(false);
    }
  };

  return (
    <div className={PAGE}>
      <div className="mb-6">
        <PageHeader title="Voice Calls" />
      </div>

      <div className="flex gap-1 mb-4 border-b border-[#D8DDE6]">
        {[['calls', 'Calls'], ['campaigns', 'Campaigns']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-[13px] font-semibold -mb-px border-b-2 ${tab === k ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#706E6B]'}`}>{label}</button>
        ))}
      </div>
      {tab === 'campaigns' ? <VoiceCampaignsPanel /> : (<>
      {/* Dial panel */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg p-5 mb-6 flex flex-wrap gap-3 items-center shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="text-2xl">📞</div>
        <input
          value={dialNumber}
          onChange={e => setDialNumber(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && dial()}
          placeholder="Enter phone number (e.g. +14155551234)"
          className="flex-1 border border-[#E5E5E5] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#04844B]"
        />
        <button
          onClick={dial}
          disabled={dialing || !dialNumber.trim()}
          className="bg-[#2E844A] hover:bg-[#256B3C] disabled:opacity-50 text-white px-6 py-2.5 rounded text-sm font-semibold whitespace-nowrap"
        >
          {dialing ? 'Calling…' : 'Call'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <select
          value={filter.direction}
          onChange={e => setFilter(f => ({ ...f, direction: e.target.value }))}
          className="border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none"
        >
          <option value="">All directions</option>
          <option value="outbound">Outbound</option>
          <option value="inbound">Inbound</option>
        </select>
        <select
          value={filter.status}
          onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
          className="border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none"
        >
          <option value="">All statuses</option>
          {['completed', 'missed', 'in-progress', 'failed', 'no-answer'].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Call list */}
      {loading && <p className="text-[#706E6B] text-sm text-center py-8">Loading…</p>}
      <div className="space-y-2">
        {calls.map(call => (
          <div
            key={call._id}
            onClick={() => setSelectedCall(call)}
            className="bg-white border border-[#E5E5E5] rounded-lg p-4 flex items-center gap-4 cursor-pointer hover:border-[#B8DAF3] hover:shadow-sm transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
          >
            <div className="text-2xl">{call.direction === 'outbound' ? '📤' : '📥'}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-[#2E2E2E]">
                  {call.direction === 'outbound' ? call.to : call.from}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[call.status] || 'bg-[#F3F2F2] text-[#706E6B]'}`}>
                  {call.status}
                </span>
                {call.sentiment && <span className="text-sm">{SENTIMENT_ICON[call.sentiment]}</span>}
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                <p className="text-xs text-[#706E6B]">{new Date(call.createdAt).toLocaleString()}</p>
                {call.duration > 0 && (
                  <p className="text-xs text-[#706E6B]">
                    {Math.floor(call.duration / 60)}:{String(call.duration % 60).padStart(2, '0')}
                  </p>
                )}
                {call.agentId && (
                  <p className="text-xs text-[#706E6B]">
                    {call.agentId.firstName || call.agentId.email}
                  </p>
                )}
              </div>
              {call.summary && <p className="text-xs text-[#706E6B] mt-1 truncate">{call.summary}</p>}
            </div>
            <div className="flex items-center gap-2 text-[#706E6B]">
              {call.recordingLink && <span className="text-xs">🎙</span>}
              {call.transcript && <span className="text-xs">📝</span>}
              <span className="text-xs">›</span>
            </div>
          </div>
        ))}
        {!loading && calls.length === 0 && (
          <div className="text-center py-16 border-2 border-dashed border-[#E5E5E5] rounded-lg text-[#706E6B]">
            <p className="text-lg mb-1">No calls yet</p>
            <p className="text-sm">Use the dial pad above to make your first call</p>
          </div>
        )}
      </div>

      </>)}

      {selectedCall && <CallModal call={selectedCall} onClose={() => setSelectedCall(null)} />}
    </div>
  );
}
